import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { adminApi } from '../api';
import {
  LogOut, Package, Trophy, Handshake, TrendingUp, RefreshCw, Search, Ticket,
  Plus, Edit3, Trash2, Save, X, Check, Truck, Clock, CheckCircle2, XCircle, Download, BarChart3,
  Activity, MessageSquare
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import ReportsSection from '../components/admin/ReportsSection';
import AnalyticsSection from '../components/admin/AnalyticsSection';

const tabs = [
  { id: 'reports',    label: 'Reports',            icon: BarChart3 },
  { id: 'analytics',  label: 'Site Traffic',       icon: Activity },
  { id: 'orders',     label: 'Orders',             icon: Package },
  { id: 'complaints', label: 'Complaints',         icon: MessageSquare },
  { id: 'entries',    label: 'Contest Entries',    icon: Trophy },
  { id: 'partners',   label: 'Partner Enquiries',  icon: Handshake },
  { id: 'coupons',    label: 'Coupons',            icon: Ticket },
];

const STATUS_META = {
  pending:    { label: 'Pending',    color: '#F4A300', bg: '#FFF1CC', icon: Clock },
  confirmed:  { label: 'Confirmed',  color: '#2A9D8F', bg: '#D8F0EC', icon: CheckCircle2 },
  dispatched: { label: 'Dispatched', color: '#4A90E2', bg: '#DBEBFA', icon: Truck },
  delivered:  { label: 'Delivered',  color: '#2A9D8F', bg: '#D8F0EC', icon: Package },
  cancelled:  { label: 'Cancelled',  color: '#E63946', bg: '#FFE4E1', icon: XCircle },
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="rounded-2xl crunch-shadow-sm p-4 md:p-5" style={{ background: color }}>
    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest opacity-80"><Icon size={14} /> {label}</div>
    <div className="font-display text-3xl md:text-4xl mt-2">{value}</div>
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('reports');
  const [stats, setStats] = useState({ orders: 0, contest_entries: 0, partners: 0, revenue: 0 });
  const [orders, setOrders] = useState([]);
  const [entries, setEntries] = useState([]);
  const [partners, setPartners] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [couponSummary, setCouponSummary] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [q, setQ] = useState('');
  const [couponForm, setCouponForm] = useState(null);
  const email = localStorage.getItem('trucrunch_admin_email');

  const load = async () => {
    setLoading(true);
    try {
      const [s, o, e, p, ins, comp] = await Promise.all([
        adminApi.stats(), adminApi.orders(), adminApi.entries(), adminApi.partners(), adminApi.couponInsights(), adminApi.complaints()
      ]);
      setStats(s); setOrders(o); setEntries(e); setPartners(p);
      setCoupons(ins.coupons || []);
      setCouponSummary(ins.summary || null);
      setComplaints(comp);
    } catch (err) {
      if (err?.response?.status === 401) navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async (kind) => {
    setExporting(true);
    try {
      await adminApi.exportCsv(kind);
      toast({ title: 'Downloaded', description: `${kind}.csv saved.` });
    } catch (err) {
      toast({ title: 'Export failed', description: err?.response?.data?.detail || 'Try again' });
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('trucrunch_admin_token')) {
      navigate('/admin/login');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem('trucrunch_admin_token');
    localStorage.removeItem('trucrunch_admin_email');
    navigate('/admin/login');
  };

  const deleteEntry = async (e) => {
    if (!window.confirm(`Delete ${e.name}'s score of ${e.score} (${e.email})? This removes it from the leaderboard.`)) return;
    try {
      await adminApi.deleteEntry(e.email);
      setEntries((cur) => cur.filter((x) => x.email !== e.email));
      setStats((s) => ({ ...s, contest_entries: Math.max(0, s.contest_entries - 1) }));
      toast({ title: 'Entry deleted', description: `${e.email} removed from the leaderboard.` });
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again' });
    }
  };

  const filter = (rows, fields) => rows.filter((r) => !q || fields.some((f) => (r[f] || '').toString().toLowerCase().includes(q.toLowerCase())));
  const fmtDate = (d) => new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

  const patchOrder = async (id, payload) => {
    try {
      const updated = await adminApi.updateOrder(id, payload);
      setOrders((prev) => prev.map((o) => o.id === id ? updated : o));
      toast({ title: 'Order updated', description: `Status: ${updated.status}` });
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || 'Try again' });
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF4E6]">
      <header className="bg-[#1a1a1a] text-[#FFF4E6]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="bg-[#FFF4E6] rounded-xl px-3 py-2">
              <img src="https://customer-assets-cm19k8pv.emergentagent.net/job_trucrunch-games/artifacts/0ug68r65_download.png" alt="TruCrunch" className="h-8 w-auto" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Admin</div>
              <div className="font-display text-xl">Dashboard</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden md:block font-brico text-sm opacity-70">{email}</div>
            <button onClick={load} className="p-2 rounded-full bg-white/10 hover:bg-white/20"><RefreshCw size={16} /></button>
            <button onClick={logout} className="btn-chunky primary text-xs"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Revenue"           value={`₹${stats.revenue}`}    icon={TrendingUp} color="#FFD84D" />
          <StatCard label="Orders"            value={stats.orders}           icon={Package}    color="#FFE9C4" />
          <StatCard label="Contest Entries"   value={stats.contest_entries}  icon={Trophy}     color="#D8F0EC" />
          <StatCard label="Partner Enquiries" value={stats.partners}         icon={Handshake}  color="#FFE4E1" />
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {tabs.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 rounded-full text-sm font-bold uppercase tracking-widest flex items-center gap-2 crunch-shadow-sm ${tab === t.id ? 'bg-[#1a1a1a] text-[#FFF4E6]' : 'bg-white'}`}>
                <t.icon size={14} /> {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {tab === 'coupons' && (
              <button onClick={() => setCouponForm({ code: '', type: 'percent', value: 10, min_order: 0, max_redemptions: 0, active: true, description: '' })} className="btn-chunky primary text-xs">
                <Plus size={14} /> New Coupon
              </button>
            )}
            <button onClick={() => exportCsv(tab)} disabled={exporting || tab === 'reports' || tab === 'analytics' || tab === 'complaints'} className={`btn-chunky outline text-xs ${['reports','analytics','complaints'].includes(tab) ? 'opacity-40 pointer-events-none' : ''}`}>
              <Download size={14} /> {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
            <div className="flex items-center gap-2 bg-white rounded-full px-3 py-2 crunch-shadow-sm">
              <Search size={16} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="font-brico bg-transparent focus:outline-none w-40 md:w-64" />
            </div>
          </div>
        </div>

        <div className="mt-5">
          {loading ? (
            <div className="p-10 text-center font-brico opacity-70 bg-white rounded-3xl crunch-shadow">Loading…</div>
          ) : tab === 'reports' ? (
            <ReportsSection />
          ) : tab === 'analytics' ? (
            <AnalyticsSection />
          ) : tab === 'orders' ? (
            <OrdersTable rows={filter(orders, ['name', 'email', 'city', 'phone', 'id', 'status', 'tracking_id'])} fmt={fmtDate} onUpdate={patchOrder} />
          ) : tab === 'complaints' ? (
            <ComplaintsTable
              rows={filter(complaints, ['name', 'email', 'subject', 'order_id', 'status'])}
              fmt={fmtDate}
              onStatusChange={async (id, status) => {
                await adminApi.updateComplaint(id, status);
                setComplaints((prev) => prev.map((c) => c.id === id ? { ...c, status } : c));
                toast({ title: 'Complaint updated', description: `Status: ${status}` });
              }}
            />
          ) : tab === 'entries' ? (
            <EntriesTable rows={filter(entries, ['name', 'email', 'city'])} fmt={fmtDate} onDelete={deleteEntry} />
          ) : tab === 'partners' ? (
            <PartnersTable rows={filter(partners, ['company', 'person', 'email', 'city', 'type'])} fmt={fmtDate} />
          ) : (
            <>
              {couponSummary && (
                <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="rounded-2xl crunch-shadow-sm p-3 md:p-4 bg-[#FFD84D]">
                    <div className="text-[10px] font-bold uppercase tracking-widest opacity-80">Coupon Revenue</div>
                    <div className="font-display text-2xl md:text-3xl mt-1">₹{couponSummary.coupon_revenue}</div>
                  </div>
                  <div className="rounded-2xl crunch-shadow-sm p-3 md:p-4 bg-[#D8F0EC]">
                    <div className="text-[10px] font-bold uppercase tracking-widest opacity-80">Coupon Orders</div>
                    <div className="font-display text-2xl md:text-3xl mt-1">{couponSummary.coupon_orders}</div>
                  </div>
                  <div className="rounded-2xl crunch-shadow-sm p-3 md:p-4 bg-[#FFE4E1]">
                    <div className="text-[10px] font-bold uppercase tracking-widest opacity-80">Discount Given</div>
                    <div className="font-display text-2xl md:text-3xl mt-1">₹{couponSummary.total_discount_given}</div>
                  </div>
                  <div className="rounded-2xl crunch-shadow-sm p-3 md:p-4 bg-white">
                    <div className="text-[10px] font-bold uppercase tracking-widest opacity-80">% of Revenue</div>
                    <div className="font-display text-2xl md:text-3xl mt-1">
                      {couponSummary.total_revenue > 0 ? Math.round((couponSummary.coupon_revenue / couponSummary.total_revenue) * 100) : 0}%
                    </div>
                  </div>
                </div>
              )}
              <CouponsTable
                rows={filter(coupons, ['code', 'description'])}
                fmt={fmtDate}
                onEdit={setCouponForm}
                onDelete={async (id) => {
                  if (!window.confirm('Delete this coupon?')) return;
                  await adminApi.deleteCoupon(id);
                  setCoupons((p) => p.filter((c) => c.id !== id));
                  toast({ title: 'Coupon deleted' });
                }}
                onToggle={async (c) => {
                  const updated = await adminApi.updateCoupon(c.id, { active: !c.active });
                  setCoupons((prev) => prev.map((x) => x.id === c.id ? { ...updated, orders_count: x.orders_count, revenue: x.revenue, total_discount: x.total_discount } : x));
                }}
              />
            </>
          )}
        </div>
      </main>

      {couponForm && (
        <CouponModal
          initial={couponForm}
          onClose={() => setCouponForm(null)}
          onSaved={(c, isNew) => {
            setCoupons((prev) => isNew ? [c, ...prev] : prev.map((x) => x.id === c.id ? c : x));
            setCouponForm(null);
          }}
        />
      )}
    </div>
  );
};

const Th = ({ children }) => <th className="text-left font-brico text-xs uppercase tracking-widest px-4 py-3 opacity-70">{children}</th>;
const Td = ({ children, className = '' }) => <td className={`px-4 py-3 font-brico text-sm border-t border-black/10 align-top ${className}`}>{children}</td>;

const OrdersTable = ({ rows, fmt, onUpdate }) => (
  <div className="bg-white rounded-3xl crunch-shadow overflow-hidden">
    <div className="overflow-x-auto">
      {rows.length === 0 ? (
        <div className="p-10 text-center font-brico opacity-70">No orders yet.</div>
      ) : (
        <table className="min-w-full">
          <thead className="bg-[#FFF4E6]"><tr>
            <Th>Order</Th><Th>Customer</Th><Th>Contact</Th><Th>Items</Th><Th>Total</Th><Th>Status</Th><Th>Actions</Th><Th>Date</Th>
          </tr></thead>
          <tbody>
            {rows.map((o) => <OrderRow key={o.id} o={o} fmt={fmt} onUpdate={onUpdate} />)}
          </tbody>
        </table>
      )}
    </div>
  </div>
);

const OrderRow = ({ o, fmt, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [tracking, setTracking] = useState({ tracking_id: o.tracking_id || '', courier: o.courier || '' });
  const meta = STATUS_META[o.status] || STATUS_META.pending;
  const StatusIcon = meta.icon;

  const setStatus = async (nextStatus) => {
    if (nextStatus === 'dispatched') {
      setEditing(true);
      return;
    }
    await onUpdate(o.id, { status: nextStatus });
  };

  const saveDispatch = async () => {
    if (!tracking.tracking_id || !tracking.courier) {
      toast({ title: 'Missing tracking info', description: 'Enter courier & tracking ID.' });
      return;
    }
    await onUpdate(o.id, { status: 'dispatched', tracking_id: tracking.tracking_id, courier: tracking.courier });
    setEditing(false);
  };

  return (
    <>
      <tr>
        <Td className="font-mono text-xs">{o.id.slice(0, 8)}…</Td>
        <Td><div className="font-bold">{o.name}</div><div className="text-xs opacity-60">{o.city} · {o.pincode}</div></Td>
        <Td><div>{o.email}</div><div className="text-xs opacity-60">{o.phone}</div></Td>
        <Td>{(o.items || []).map((i, idx) => <div key={idx} className="text-xs whitespace-nowrap">{i.title} ×{i.qty}</div>)}</Td>
        <Td className="font-display">
          ₹{o.total}
          {o.discount > 0 && <div className="text-[10px] text-[#2A9D8F] font-brico font-bold">-₹{o.discount} {o.coupon_code}</div>}
        </Td>
        <Td>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold uppercase" style={{ background: meta.bg, color: meta.color }}>
            <StatusIcon size={12} /> {meta.label}
          </span>
          {o.tracking_id && <div className="text-[10px] mt-1 opacity-70 font-mono">{o.courier} · {o.tracking_id}</div>}
        </Td>
        <Td>
          <select
            value={o.status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-2 py-1 rounded-lg border border-black/20 bg-white text-xs font-bold uppercase font-brico"
          >
            {Object.keys(STATUS_META).map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
          {o.status === 'dispatched' && !editing && (
            <button onClick={() => setEditing(true)} className="ml-2 text-xs font-bold text-[#4A90E2] underline">Edit tracking</button>
          )}
        </Td>
        <Td className="text-xs opacity-70 whitespace-nowrap">{fmt(o.created_at)}</Td>
      </tr>
      {editing && (
        <tr>
          <td colSpan={8} className="bg-[#FFF4E6] border-t border-black/10 px-4 py-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-widest">Dispatch details:</span>
              <input value={tracking.courier} onChange={(e) => setTracking({ ...tracking, courier: e.target.value })} placeholder="Courier (e.g. Delhivery)" className="px-3 py-2 rounded-lg border-2 border-[#1a1a1a] text-sm font-brico bg-white" />
              <input value={tracking.tracking_id} onChange={(e) => setTracking({ ...tracking, tracking_id: e.target.value })} placeholder="Tracking ID" className="px-3 py-2 rounded-lg border-2 border-[#1a1a1a] text-sm font-brico bg-white font-mono" />
              <button onClick={saveDispatch} className="btn-chunky primary text-xs"><Save size={12} /> Mark Dispatched</button>
              <button onClick={() => setEditing(false)} className="btn-chunky outline text-xs"><X size={12} /> Cancel</button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const EntriesTable = ({ rows, fmt, onDelete }) => (
  <div className="bg-white rounded-3xl crunch-shadow overflow-hidden">
    <div className="overflow-x-auto">
      {rows.length === 0 ? (
        <div className="p-10 text-center font-brico opacity-70">No contest entries yet.</div>
      ) : (
        <table className="min-w-full">
          <thead className="bg-[#FFF4E6]"><tr><Th>#</Th><Th>Name</Th><Th>Email</Th><Th>City</Th><Th>Score</Th><Th>Date</Th><Th></Th></tr></thead>
          <tbody>
            {rows.map((e, i) => (
              <tr key={e.id || i}>
                <Td className="font-display">#{i + 1}</Td>
                <Td className="font-bold">{e.name}</Td>
                <Td>{e.email}</Td>
                <Td>{e.city}</Td>
                <Td className="font-display text-lg">{e.score}</Td>
                <Td className="text-xs opacity-70">{fmt(e.created_at)}</Td>
                <Td>
                  <button data-testid={`delete-entry-${e.email}`} onClick={() => onDelete(e)} aria-label="Delete entry"
                    className="p-2 rounded-full text-[#E63946] hover:bg-[#FFE4E1] transition-colors">
                    <Trash2 size={16} />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
);

const PartnersTable = ({ rows, fmt }) => (
  <div className="bg-white rounded-3xl crunch-shadow overflow-hidden">
    <div className="overflow-x-auto">
      {rows.length === 0 ? (
        <div className="p-10 text-center font-brico opacity-70">No partner enquiries yet.</div>
      ) : (
        <table className="min-w-full">
          <thead className="bg-[#FFF4E6]"><tr><Th>Company</Th><Th>Contact</Th><Th>Email</Th><Th>Phone</Th><Th>City</Th><Th>Type</Th><Th>Volume</Th><Th>Notes</Th><Th>Date</Th></tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id}>
                <Td className="font-bold">{p.company}</Td>
                <Td>{p.person}</Td>
                <Td>{p.email}</Td>
                <Td>{p.phone}</Td>
                <Td>{p.city}</Td>
                <Td><span className="px-2 py-1 rounded-full bg-[#D8F0EC] text-xs font-bold uppercase">{p.type}</span></Td>
                <Td>{p.volume}</Td>
                <Td className="max-w-xs text-xs">{p.notes}</Td>
                <Td className="text-xs opacity-70">{fmt(p.created_at)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
);

const CouponsTable = ({ rows, fmt, onEdit, onDelete, onToggle }) => (
  <div className="bg-white rounded-3xl crunch-shadow overflow-hidden">
    <div className="overflow-x-auto">
      {rows.length === 0 ? (
        <div className="p-10 text-center font-brico opacity-70">No coupons yet. Click <b>New Coupon</b> to create one.</div>
      ) : (
        <table className="min-w-full">
          <thead className="bg-[#FFF4E6]"><tr>
            <Th>Code</Th><Th>Discount</Th><Th>Min. Order</Th><Th>Validity</Th><Th>Usage</Th><Th>Orders</Th><Th>Revenue</Th><Th>Discount Given</Th><Th>Active</Th><Th>Actions</Th>
          </tr></thead>
          <tbody>
            {rows.map((c) => {
              const isPct = c.type === 'percent';
              const usage = c.max_redemptions ? `${c.redemptions || 0} / ${c.max_redemptions}` : `${c.redemptions || 0}`;
              const validity = [c.starts_at ? new Date(c.starts_at).toLocaleDateString('en-IN') : '—', c.ends_at ? new Date(c.ends_at).toLocaleDateString('en-IN') : '—'].join(' → ');
              return (
                <tr key={c.id}>
                  <Td>
                    <div className="font-display text-lg">{c.code}</div>
                    {c.description && <div className="text-xs opacity-60">{c.description}</div>}
                  </Td>
                  <Td className="font-brico font-bold">{isPct ? `${c.value}%` : `₹${c.value} OFF`}</Td>
                  <Td>{c.min_order ? `₹${c.min_order}` : '—'}</Td>
                  <Td className="text-xs">{validity}</Td>
                  <Td>{usage}</Td>
                  <Td className="font-display text-lg">{c.orders_count || 0}</Td>
                  <Td className="font-display text-lg text-[#2A9D8F]">₹{c.revenue || 0}</Td>
                  <Td className="font-brico text-[#E63946]">−₹{c.total_discount || 0}</Td>
                  <Td>
                    <button onClick={() => onToggle(c)} className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${c.active ? 'bg-[#2A9D8F] text-white' : 'bg-black/10'}`}>
                      {c.active ? 'Active' : 'Inactive'}
                    </button>
                  </Td>
                  <Td>
                    <button onClick={() => onEdit(c)} className="p-2 rounded-full hover:bg-black/5"><Edit3 size={14} /></button>
                    <button onClick={() => onDelete(c.id)} className="p-2 rounded-full hover:bg-black/5 text-[#E63946]"><Trash2 size={14} /></button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  </div>
);

const toDateInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60 * 1000).toISOString().slice(0, 10);
};

const CouponModal = ({ initial, onClose, onSaved }) => {
  const isNew = !initial.id;
  const [f, setF] = useState({
    code: initial.code || '',
    type: initial.type || 'percent',
    value: initial.value ?? 10,
    min_order: initial.min_order ?? 0,
    starts_at: toDateInput(initial.starts_at),
    ends_at: toDateInput(initial.ends_at),
    max_redemptions: initial.max_redemptions ?? 0,
    per_user_limit: initial.per_user_limit ?? 0,
    active: initial.active ?? true,
    description: initial.description || '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...f,
        code: f.code.trim().toUpperCase(),
        value: Number(f.value),
        min_order: Number(f.min_order),
        max_redemptions: Number(f.max_redemptions),
        per_user_limit: Number(f.per_user_limit),
        starts_at: f.starts_at ? new Date(f.starts_at).toISOString() : null,
        ends_at: f.ends_at ? new Date(f.ends_at + 'T23:59:59').toISOString() : null,
      };
      const res = isNew ? await adminApi.createCoupon(payload) : await adminApi.updateCoupon(initial.id, payload);
      onSaved(res, isNew);
      toast({ title: isNew ? 'Coupon created' : 'Coupon updated' });
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again' });
    } finally {
      setSaving(false);
    }
  };

  const input = 'w-full px-3 py-2 rounded-xl border-2 border-[#1a1a1a] font-brico bg-white focus:outline-none focus:ring-2 focus:ring-[#E63946]';

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#FFF4E6] rounded-3xl crunch-shadow p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="text-xs font-bold uppercase tracking-widest text-[#E63946]">Coupon</div>
        <h3 className="font-display text-3xl uppercase mt-1">{isNew ? 'New Coupon' : 'Edit Coupon'}</h3>

        <div className="mt-5 space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest opacity-70">Code</label>
            <input required value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="CRUNCH10" className={`${input} uppercase font-mono`} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest opacity-70">Description (optional)</label>
            <input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="e.g. Launch offer" className={input} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest opacity-70">Type</label>
              <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={input}>
                <option value="percent">Percent %</option>
                <option value="flat">Flat ₹</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest opacity-70">Value {f.type === 'percent' ? '(%)' : '(₹)'}</label>
              <input required type="number" min={1} max={f.type === 'percent' ? 100 : undefined} value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} className={input} />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest opacity-70">Minimum order (₹)</label>
            <input type="number" min={0} value={f.min_order} onChange={(e) => setF({ ...f, min_order: e.target.value })} className={input} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest opacity-70">Starts</label>
              <input type="date" value={f.starts_at} onChange={(e) => setF({ ...f, starts_at: e.target.value })} className={input} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest opacity-70">Ends</label>
              <input type="date" value={f.ends_at} onChange={(e) => setF({ ...f, ends_at: e.target.value })} className={input} />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest opacity-70">Max redemptions (0 = unlimited)</label>
            <input type="number" min={0} value={f.max_redemptions} onChange={(e) => setF({ ...f, max_redemptions: e.target.value })} className={input} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest opacity-70">Uses per user (0 = unlimited)</label>
            <div className="flex items-center gap-3 mt-1">
              <input type="number" min={0} value={f.per_user_limit} onChange={(e) => setF({ ...f, per_user_limit: e.target.value })} className={input} style={{ maxWidth: 120 }} />
              <div className="flex items-center gap-3 text-sm font-brico">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" checked={Number(f.per_user_limit) === 0} onChange={() => setF({ ...f, per_user_limit: 0 })} /> Unlimited
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" checked={Number(f.per_user_limit) === 1} onChange={() => setF({ ...f, per_user_limit: 1 })} /> Once per user
                </label>
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 font-brico font-bold">
            <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="w-5 h-5" />
            Active
          </label>
        </div>

        <div className="mt-6 flex gap-3">
          <button type="button" onClick={onClose} className="btn-chunky outline flex-1 justify-center"><X size={14} /> Cancel</button>
          <button type="submit" disabled={saving} className="btn-chunky primary flex-1 justify-center"><Save size={14} /> {saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
};

const ComplaintsTable = ({ rows, fmt, onStatusChange }) => (
  <div className="bg-white rounded-3xl crunch-shadow overflow-hidden">
    <div className="overflow-x-auto">
      {rows.length === 0 ? (
        <div className="p-10 text-center font-brico opacity-70">No complaints yet.</div>
      ) : (
        <table className="min-w-full">
          <thead className="bg-[#FFF4E6]"><tr>
            <Th>Subject</Th><Th>From</Th><Th>Order</Th><Th>Message</Th><Th>Status</Th><Th>Received</Th>
          </tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id}>
                <Td><div className="font-bold">{c.subject}</div></Td>
                <Td>
                  <div className="font-bold">{c.name}</div>
                  <div className="text-xs opacity-70">{c.email}</div>
                  {c.phone && <div className="text-xs opacity-60">{c.phone}</div>}
                </Td>
                <Td className="font-mono text-xs">{c.order_id || '—'}</Td>
                <Td className="max-w-md text-xs whitespace-pre-wrap">{c.message}</Td>
                <Td>
                  <select
                    value={c.status || 'open'}
                    onChange={(e) => onStatusChange(c.id, e.target.value)}
                    className="px-2 py-1 rounded-lg border border-black/20 bg-white text-xs font-bold uppercase font-brico"
                  >
                    {['open', 'in_progress', 'resolved', 'closed'].map((s) => (
                      <option key={s} value={s}>{s.replace('_', ' ')}</option>
                    ))}
                  </select>
                </Td>
                <Td className="text-xs opacity-70 whitespace-nowrap">{fmt(c.created_at)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
);

export default AdminDashboard;
